import { inject } from '@angular/core';
import { MatDateFormats } from '@angular/material/core';
import { SettingsStore } from 'src/app/core/store/settings.store';

/**
 * Long and explicit whatever the visible style is — a screen reader should
 * never have to disambiguate "03/04/2026".
 */
const A11Y_DATE = 'MMMM d, yyyy';
const A11Y_MONTH_YEAR = 'MMMM yyyy';

/**
 * The calendar's own period header, left on Material's convention. `dateStyle`
 * governs how a date is *written* — the month a picker is currently paging
 * through is chrome, and abbreviating it is what keeps the header on one line.
 */
const MONTH_YEAR_LABEL = 'MMM yyyy';

interface SettingsDateFormatsOptions {
  /**
   * Use the day-and-month token instead of the full date, for the filter bars
   * whose inputs sit in a chip rather than a form field.
   */
  compact?: boolean;
}

/**
 * `MAT_DATE_FORMATS` is resolved once per injector and then read on every
 * render, so the tokens have to be getters rather than values: a snapshot taken
 * at bootstrap would pin whatever the preference was before the profile even
 * arrived, and never move again. Reading the signal inside the getter is what
 * makes a format change reach the next render.
 *
 * The timepicker formats are always present because MatTimepicker throws
 * outright without them, and a datepicker that shows no time ignores them.
 *
 * Must be called from an injection context — as a `useFactory`.
 */
export const settingsDateFormats = ({
  compact = false,
}: SettingsDateFormatsOptions = {}): MatDateFormats => {
  const settings = inject(SettingsStore);
  const dateToken = () =>
    compact ? settings.dayMonthToken() : settings.dateToken();

  return {
    parse: {
      get dateInput() {
        return dateToken();
      },
      get timeInput() {
        return settings.timeToken();
      },
    },
    display: {
      get dateInput() {
        return dateToken();
      },
      monthYearLabel: MONTH_YEAR_LABEL,
      dateA11yLabel: A11Y_DATE,
      monthYearA11yLabel: A11Y_MONTH_YEAR,
      get timeInput() {
        return settings.timeToken();
      },
      get timeOptionLabel() {
        return settings.timeToken();
      },
    },
  };
};
